import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaArrowDown, FaFileAlt } from 'react-icons/fa';
import gsap from 'gsap';
import { useTheme } from '../context/ThemeContext';

const HERO_BG = '/assets/escritorio.jpg';

const Hero = () => {
  const { isDark } = useTheme();
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const whatsappNumber = '5514991784044';
  const whatsappMessage = encodeURIComponent(
    'Olá! Gostaria de solicitar um orçamento para desenvolvimento de site institucional.'
  );

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.hero-line', {
        y: 100,
        opacity: 0,
        duration: 1.2,
        stagger: 0.15,
        ease: 'power4.out',
        delay: 0.3,
      });

      gsap.from('.hero-cta', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        delay: 1.2,
      });

      gsap.to('.hero-glow', {
        scale: 1.2,
        opacity: 0.6,
        duration: 4,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  return (
    <section
      ref={heroRef}
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-500"
    >
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className={`absolute inset-0 z-[1] ${isDark ? 'bg-dark-bg/80' : 'bg-dark/50'}`} />
      <div className="hero-gradient absolute inset-0 z-[1] opacity-40 mix-blend-multiply" />

      <div className="hero-glow absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-burnt/20 rounded-full blur-[120px] z-[2]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <motion.p
          className="hero-line text-burnt font-heading font-semibold uppercase tracking-[0.3em] text-sm mb-6"
          initial={{ opacity: 0 }}
        >
          MS Digitais · Desenvolvimento Web
        </motion.p>

        <h1 className="mb-6">
          <span className="hero-line block text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white leading-[0.95]">
            Sites que
          </span>
          <span className="hero-line block text-5xl md:text-7xl lg:text-8xl font-heading font-bold leading-[0.95]">
            <span className="text-burnt">impressionam</span>
          </span>
          <span className="hero-line block text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white leading-[0.95]">
            e convertem
          </span>
        </h1>

        <p ref={titleRef} className="hero-line text-lg md:text-xl text-cream/90 mb-4 font-body max-w-3xl mx-auto leading-relaxed">
          Crio sites institucionais modernos e personalizados que fortalecem sua presença digital
          e abrem novas oportunidades de negócio.
        </p>

        <p className="hero-line text-base md:text-lg text-cream/70 mb-12 font-body max-w-2xl mx-auto">
          Olá, sou Monise — desenvolvedora de sites e sistemas. Transformo ideias em soluções digitais de alto impacto.
        </p>

        <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleWhatsApp}
            className="btn-premium group bg-burnt hover:bg-primary-700 text-white px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-premium flex items-center gap-3"
            data-cursor="pointer"
          >
            <FaWhatsapp className="text-xl" />
            Solicitar Orçamento
          </button>
          <Link
            to="/orcamento"
            className="group px-10 py-4 rounded-xl font-semibold text-lg text-white border border-white/30 hover:border-burnt hover:bg-burnt/10 transition-all duration-300 flex items-center gap-2"
            data-cursor="pointer"
          >
            <FaFileAlt />
            Preencher formulário
          </Link>
          <a
            href="#projetos"
            className="group px-10 py-4 rounded-xl font-semibold text-lg text-white/90 border border-white/20 hover:border-white/40 hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
            data-cursor="pointer"
          >
            Ver Portfólio
          </a>
        </div>

        <motion.div
          className="mt-24"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <FaArrowDown className="w-5 h-5 mx-auto text-cream/50" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
